import {
  faCalendarAlt,
  faCalendarCheck,
  faCalendarTimes,
  faIdCard,
  faPlus,
  faCheckCircle,
  faExclamationTriangle,
  faTimesCircle,
  faTrash,
  faStar,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDispatch } from 'react-redux';
import { useState } from 'react';
import cn from 'classnames';
import { deleteSubscription } from '@redux/features/subscriptionsSlice';
import { formatDate } from 'helpers/formatDate';
import { ConfirmDeletionModal } from 'components/ConfirmDeletionModal';
import { Button } from 'components/Button';
import { Modal } from 'components/Modal';
import { SubscriptionForm } from './SubscriptionForm';
import styles from './SubscriptionManager.scss';

export const SubscriptionManager = ({ clientID, subscriptions }) => {
  const [isConfirmDeletionOpen, setIsConfirmDeletionOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const dispatch = useDispatch();

  const getSubscriptionTypeLabel = (type) => {
    return type === 'membership' ? 'Членство' : 'Абонемент';
  };

  const getStatusInfo = (status) => {
    switch (status) {
      case 'active':
        return {
          label: 'Активно',
          icon: faCheckCircle,
          className: styles.active,
        };
      case 'expired':
        return {
          label: 'Закінчено',
          icon: faTimesCircle,
          className: styles.expired,
        };
    }
  };

  const getDaysUntilExpiry = (endDate) => {
    const today = new Date();
    const end = new Date(endDate);
    const diffTime = end - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getExpiryWarning = (endDate) => {
    const daysLeft = getDaysUntilExpiry(endDate);

    if (daysLeft < 0) return null;

    if (daysLeft === 0) {
      return {
        text: 'Закінчується сьогодні',
        className: styles.warningCritical,
      };
    } else if (daysLeft <= 3) {
      return {
        text: `Закінчується через ${daysLeft} ${daysLeft === 1 ? 'день' : 'дні'}`,
        className: styles.warningCritical,
      };
    } else if (daysLeft <= 7) {
      return {
        text: `Закінчується через ${daysLeft} днів`,
        className: styles.warningMedium,
      };
    } else if (daysLeft <= 14) {
      return {
        text: `Закінчується через ${daysLeft} днів`,
        className: styles.warningLow,
      };
    }

    return null;
  };

  const handleDelete = () => dispatch(deleteSubscription(selectedSubscription.id));

  const handleDeleteClick = (subscription) => {
    setSelectedSubscription(subscription);
    setIsConfirmDeletionOpen(true);
  };

  return (
    <div className={styles.subscriptionsContainer}>
      <div className={styles.headerSection}>
        <h3 className={styles.sectionTitle}>Пакети клієнта</h3>
        <Button onClick={() => setIsCreateModalOpen(true)} className={styles.createButton} smallStyle>
          <FontAwesomeIcon icon={faPlus} />
          Створити пакет
        </Button>
      </div>

      {subscriptions && subscriptions.length > 0 ? (
        <div className={styles.subscriptionsList}>
          {subscriptions.map((subscription) => {
            const statusInfo = getStatusInfo(subscription.status);
            const expiryWarning = getExpiryWarning(subscription.endDate);

            return (
              <div key={subscription.id} className={styles.subscriptionCard}>
                {expiryWarning && (
                  <div className={cn(styles.warning, expiryWarning.className)}>
                    <FontAwesomeIcon icon={faExclamationTriangle} />
                    <span>{expiryWarning.text}</span>
                  </div>
                )}
                <div className={styles.header}>
                  <div className={styles.typeAndStatus}>
                    <div className={styles.type}>
                      <FontAwesomeIcon
                        icon={subscription.type === 'membership' ? faStar : faIdCard}
                        className={styles.typeIcon}
                      />
                      <span className={styles.typeLabel}>{getSubscriptionTypeLabel(subscription.type)}</span>
                    </div>
                    <div className={cn(styles.status, statusInfo.className)}>
                      <FontAwesomeIcon icon={statusInfo.icon} />
                      <span>{statusInfo.label}</span>
                    </div>
                  </div>
                  <div className={styles.actions}>
                    <Button
                      className={cn(styles.actionBtn, styles.deleteBtn)}
                      onClick={() => handleDeleteClick(subscription)}
                      noStyle
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </Button>
                  </div>
                </div>

                <div className={styles.dateSection}>
                  <div className={styles.dateItem}>
                    <FontAwesomeIcon icon={faCalendarAlt} className={styles.dateIcon} />
                    <div>
                      <span className={styles.dateLabel}>Початок</span>
                      <span className={styles.dateValue}>{formatDate(subscription.startDate, 'UA', true)}</span>
                    </div>
                  </div>
                  <div className={styles.dateSeparator}>—</div>
                  <div className={styles.dateItem}>
                    <FontAwesomeIcon
                      icon={subscription.status === 'expired' ? faCalendarTimes : faCalendarCheck}
                      className={cn(styles.dateIcon, {
                        [styles.expired]: subscription.status === 'expired',
                      })}
                    />
                    <div>
                      <span className={styles.dateLabel}>Закінчення</span>
                      <span className={styles.dateValue}>{formatDate(subscription.endDate, 'UA', true)}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className={cn(styles.subscriptionCard, styles.noSubscription)}>
          <FontAwesomeIcon icon={faIdCard} className={styles.noSubscriptionIcon} />
          <h3 className={styles.noSubscriptionTitle}>У клієнта ще немає пакетів</h3>
        </div>
      )}

      <Modal isOpen={isCreateModalOpen} setIsOpen={setIsCreateModalOpen} size='medium'>
        <h3>Створити пакет</h3>
        <SubscriptionForm clientID={clientID} onClose={() => setIsCreateModalOpen(false)} />
      </Modal>

      <ConfirmDeletionModal
        isOpen={isConfirmDeletionOpen}
        setIsOpen={setIsConfirmDeletionOpen}
        elementName='пакет'
        onConfirm={handleDelete}
      />
    </div>
  );
};
